<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    appendTo="body"
    :baseZIndex="baseZIndex"
    :closable="!saving"
    :dismissableMask="!saving"
    :draggable="false"
    :style="{ width: '28rem' }"
    :breakpoints="{ '960px': '90vw', '575px': '98vw' }"
    :pt="crcFormDialogPt"
    @hide="onClose"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div class="crc-form-dialog-header__icon bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
          <i class="pi pi-map-marker text-base" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">
            {{ modalTitle }}
          </div>
        </div>
      </div>
    </template>

    <form @submit.prevent="handleSubmit">
      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-fields">
          <FloatLabel variant="on" class="w-full">
            <InputText
              id="location-edit-name"
              v-model="draft.name"
              :disabled="saving"
              maxlength="160"
              autocomplete="off"
              :invalid="submitted && !draft.name?.trim()"
              class="w-full rounded-md"
            />
            <label for="location-edit-name" class="text-sm font-medium">
              Name <span class="text-red-500">*</span>
            </label>
          </FloatLabel>
          <small v-if="submitted && !draft.name?.trim()" class="text-xs text-red-500 pl-1">
            Please enter a location name.
          </small>

          <FloatLabel variant="on" class="w-full">
            <Select
              inputId="location-edit-type"
              v-model="draft.type"
              :options="locationTypeOptions"
              optionLabel="label"
              optionValue="value"
              :disabled="saving"
              class="w-full"
              inputClass="w-full rounded-md"
            />
            <label for="location-edit-type" class="text-sm font-medium">Type</label>
          </FloatLabel>
        </div>
      </section>

      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-section__label">
          Address
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="w-full sm:col-span-2">
            <CountryInputField
              input-id="location-edit-country"
              v-model="draft.country"
              label="Country"
              :locations="countryLocationSource"
              :disabled="saving"
              :can-add="true"
              :can-delete="canDeleteCountry"
              :hint="countryFieldHint"
            />
          </div>
          <FloatLabel variant="on" class="w-full">
            <InputText
              id="location-edit-city"
              v-model="draft.city"
              :disabled="saving"
              maxlength="100"
              class="w-full rounded-md"
            />
            <label for="location-edit-city" class="text-sm font-medium">City</label>
          </FloatLabel>
          <FloatLabel variant="on" class="w-full">
            <InputText
              id="location-edit-state"
              v-model="draft.state"
              :disabled="saving"
              maxlength="80"
              class="w-full rounded-md"
            />
            <label for="location-edit-state" class="text-sm font-medium">State</label>
          </FloatLabel>
          <FloatLabel variant="on" class="w-full sm:col-span-2">
            <Textarea
              id="location-edit-address"
              v-model="draft.address"
              :disabled="saving"
              rows="2"
              maxlength="1000"
              class="w-full rounded-md"
            />
            <label for="location-edit-address" class="text-sm font-medium">Street address</label>
          </FloatLabel>
        </div>
      </section>

      <section v-if="showStatus && !isNew" class="crc-form-dialog-section">
        <div class="crc-form-dialog-status">
          <div>
            <div class="crc-form-dialog-status__title">Active</div>
            <div class="crc-form-dialog-status__hint">
              Inactive locations are hidden from employee profile options.
            </div>
          </div>
          <ToggleSwitch v-model="draft.is_active" :disabled="saving" />
        </div>
      </section>

      <div class="crc-form-dialog-footer">
        <Button
          v-if="!isNew && canDelete"
          type="button"
          label="Delete"
          icon="pi pi-trash"
          severity="danger"
          text
          :disabled="saving"
          @click="handleDelete"
        />
        <div class="crc-form-dialog-footer__actions">
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            text
            :disabled="saving"
            @click="visibleProxy = false"
          />
          <Button type="submit" label="Save" icon="pi pi-check" :loading="saving" />
        </div>
      </div>
    </form>
  </Dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';
import FloatLabel from 'primevue/floatlabel';
import ToggleSwitch from 'primevue/toggleswitch';
import CountryInputField from '@/components/forms/CountryInputField.vue';
import { crcFormDialogPt } from '@/components/common/dialog/crcDialogPt.js';
import { LOCATION_TYPE_OPTIONS } from '@/constants/orgReference';
import { normalizeCountry } from '@/utils/countryCatalog';

defineOptions({ name: 'LocationEditModal' });

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  showStatus: {
    type: Boolean,
    default: true,
  },
  canDelete: {
    type: Boolean,
    default: false,
  },
  /** Country trash — Settings → Locations only, not employee location pickers. */
  canDeleteCountry: {
    type: Boolean,
    default: false,
  },
  baseZIndex: {
    type: Number,
    default: 1200,
  },
  /** Existing locations — used to suggest countries already in use. */
  locations: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:visible', 'save', 'delete']);

const locationTypeOptions = LOCATION_TYPE_OPTIONS;
const submitted = ref(false);
const addressLoaded = ref(false);

const draft = reactive({
  id: null,
  name: '',
  type: 'office',
  country: '',
  state: '',
  city: '',
  address: '',
  is_active: true,
});

const countryLocationSource = computed(() =>
  Array.isArray(props.locations) && props.locations.length
    ? props.locations
    : [{ country: draft.country }]
);

const countryFieldHint = computed(() =>
  props.canDeleteCountry
    ? 'Use + if the country is not listed yet. Trash removes it only when no work sites use it.'
    : 'Use + if the country is not listed yet.'
);

const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
});

const isNew = computed(() => draft.id == null);

const modalTitle = computed(() => (isNew.value ? 'Add Location' : 'Edit Location'));

const resetDraft = () => {
  draft.id = null;
  draft.name = '';
  draft.type = 'office';
  draft.country = '';
  draft.state = '';
  draft.city = '';
  draft.address = '';
  draft.is_active = true;
  submitted.value = false;
  addressLoaded.value = false;
};

const fillDraftFromLocation = (location) => {
  draft.id = location?.id ?? null;
  draft.name = location?.name || '';
  draft.type = location?.type || 'office';
  draft.country = normalizeCountry(location?.country);
  draft.state = location?.state || '';
  draft.city = location?.city || '';
  draft.address = location?.address || '';
  draft.is_active = location?.is_active !== false;
  submitted.value = false;
  addressLoaded.value = Object.prototype.hasOwnProperty.call(location || {}, 'address');
};

const onClose = () => {
  if (!props.saving) {
    resetDraft();
  }
};

const handleDelete = () => {
  if (isNew.value || !props.canDelete || props.saving) return;
  emit('delete', {
    id: draft.id,
    name: draft.name,
  });
};

const handleSubmit = () => {
  submitted.value = true;
  if (!draft.name?.trim()) return;

  const payload = {
    name: draft.name.trim(),
    type: draft.type,
    country: normalizeCountry(draft.country) || null,
    state: draft.state?.trim() || null,
    city: draft.city?.trim() || null,
    ...(addressLoaded.value || draft.address?.trim()
      ? { address: draft.address?.trim() || null }
      : {}),
  };

  if (isNew.value) {
    emit('save', { mode: 'new', payload });
    return;
  }

  emit('save', {
    mode: 'edit',
    payload: {
      location_id: draft.id,
      ...payload,
      ...(props.showStatus ? { is_active: draft.is_active ? 1 : 0 } : {}),
    },
  });
};

defineExpose({
  openNew: () => {
    resetDraft();
  },
  openEdit: (location) => {
    fillDraftFromLocation(location);
  },
});
</script>
